import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import {
  moedaBr,
  percentualBr,
  quantidadeBr,
  ROTULO_PAGAMENTO,
  ROTULO_TABELA,
} from '../lib/formato.js';
import {
  mesAnterior,
  mesAtual,
  mesmoPeriodo,
  periodoIncompleto,
  periodoInvertido,
  type Periodo,
} from '../lib/periodo.js';
import { obter } from '../lib/api.js';
import './Dashboard.css';

/**
 * Dashboard gerencial — Épico UX, Frente 2, Story 2.3.
 *
 * Consome `GET /api/dashboard` (Story 2.2) e não calcula **nada**: todo número
 * aqui vem pronto do servidor, como string, e só passa por formatação. Somar
 * itens no cliente criaria uma segunda verdade que diverge na terceira casa —
 * é a regra que vale no gerador e vale aqui.
 *
 * ## Quem vê
 *
 * Admin e gestor. A tela mostra **RT**, que é a comissão do representante — o
 * mesmo motivo que tirou o RT do PDF no item 01 do feedback e `ver_dashboard`
 * do vendedor na Story 2.1. A guarda real é o 403 da API; a rota e o menu só
 * evitam mostrar uma porta que não abre.
 *
 * ## As duas bases de tempo
 *
 * O handoff da 2.2 alertou, e é o ponto mais fácil de ler errado nesta tela:
 *
 * - **Faturamento** conta por `emitido_em` — *quando o dinheiro entrou*.
 * - **Funil** conta por `criado_em` — é uma **coorte**: dos orçamentos criados
 *   no período, quantos viraram emissão.
 *
 * Um orçamento criado em abril e emitido em maio aparece no faturamento de
 * maio e no funil de abril. Está certo, e é por isso que cada bloco carrega um
 * selo dizendo por qual data ele conta: sem isso o gestor soma "3 emitidos" do
 * funil com "5 orçamentos" do faturamento e conclui que o sistema erra.
 */

interface FatiaMix {
  chave: string;
  quantidade: number;
  valor: string;
}

interface ProdutoRanking {
  referencia: string;
  formato: string | null;
  /** Em quantos orçamentos emitidos ele apareceu. */
  vezes: number;
  quantidade: string;
  valor: string;
}

interface ResumoDashboard {
  periodo: { de: string; ate: string };
  faturamento: {
    totalEmitido: string;
    quantidade: number;
    ticketMedio: string;
    rtTotal: string;
    freteTotal: string;
  };
  funil: { criados: number; emitidos: number; rascunhos: number; taxaConversao: string };
  mixPorTabela: FatiaMix[];
  mixPorPagamento: FatiaMix[];
  rankingProdutos: ProdutoRanking[];
}

/*
 * `mesAtual`, `mesAnterior` e `diaIso` MUDARAM DE CASA na Story F.2 — foram
 * para `lib/periodo.ts`, sem mudança de corpo, porque a lista de orçamentos
 * passou a precisar dos mesmos cálculos (e a página de logs vai precisar).
 *
 * A reexportação abaixo existe para o teste desta tela continuar importando
 * daqui, que é onde as funções nasceram e onde faz sentido procurá-las.
 */
export { mesAnterior, mesAtual } from '../lib/periodo.js';

/*
 * Os rótulos do mix — os dois reais vêm da fonte única, e as chaves
 * especulativas ficam.
 *
 * `boleto` e `prazo` nunca apareceram em produção (só existem `avista` e
 * `cartao`); estão aqui à espera. Note que `prazo` traz o MESMO texto que
 * `cartao` agora traz — não é engano: se um dia as duas chaves coexistirem,
 * elas precisarão de nomes distintos, e a decisão é de quem as criar.
 */
const ROTULO_MIX_PAGAMENTO: Record<string, string> = {
  ...ROTULO_PAGAMENTO,
  boleto: 'Boleto',
  prazo: 'A prazo',
};

/**
 * Traduz a chave crua, **sem nunca escondê-la**.
 *
 * O fallback é a própria chave: uma forma de pagamento nova, cadastrada depois
 * desta tela, aparece como `pix` em vez de sumir do gráfico. Um mix que omite
 * uma fatia mente sobre o total, e ninguém percebe olhando.
 */
function rotulo(mapa: Record<string, string>, chave: string): string {
  return mapa[chave] ?? chave;
}

// --------------------------------------------------------------------- barras

/**
 * Barras horizontais em CSS, sem biblioteca de gráfico.
 *
 * O projeto tem cinco dependências de runtime e evita até ícone de terceiro;
 * uma lib de chart para desenhar três retângulos seria desproporcional. E
 * horizontal em vez de pizza porque comparar comprimentos é mais preciso do que
 * comparar ângulos, o rótulo cabe ao lado sem legenda, e o valor fica escrito —
 * quem lê não precisa estimar nada.
 *
 * Cada barra é `<li>` numa lista: o número está no texto, então leitor de tela
 * e busca de teste leem o mesmo que o olho vê.
 */
function Barras({
  fatias,
  mapa,
}: {
  fatias: FatiaMix[];
  mapa: Record<string, string>;
}): JSX.Element {
  /*
   * A escala é o MAIOR valor da série, não a soma: com soma, uma série de três
   * fatias parecidas vira três barras curtas e indistinguíveis. O que se
   * compara aqui é uma fatia com a outra.
   *
   * O `|| 1` protege a divisão quando toda a série é zero — sem ele, a largura
   * vira `NaN%` e o CSS descarta a propriedade, deixando barras de largura
   * cheia que sugerem valor onde não há.
   */
  const maior = Math.max(...fatias.map((f) => Number(f.valor)), 0) || 1;

  return (
    <ul className="dash__barras">
      {fatias.map((f) => (
        <li className="dash__barra" key={f.chave}>
          <span className="dash__barra-rotulo">{rotulo(mapa, f.chave)}</span>
          <span className="dash__barra-trilho">
            <span
              className="dash__barra-preenchida"
              style={{ width: `${String((Number(f.valor) / maior) * 100)}%` }}
            />
          </span>
          <span className="dash__barra-valor num">
            <small>R$</small> {moedaBr(f.valor)}
          </span>
          <span className="dash__barra-qtd num">
            {f.quantidade} {f.quantidade === 1 ? 'orçamento' : 'orçamentos'}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Um número de destaque. `unidade` fica num elemento à parte — ver `formato.ts`. */
function Cartela({
  rotulo: titulo,
  valor,
  unidade,
  nota,
}: {
  rotulo: string;
  valor: string;
  unidade?: string;
  nota?: string;
}): JSX.Element {
  return (
    <div className="dash__cartela">
      <p className="dash__cartela-rotulo">{titulo}</p>
      <p className="dash__cartela-valor">
        {unidade !== undefined && <small className="dash__cartela-unidade">{unidade}</small>}
        {valor}
      </p>
      {nota !== undefined && <p className="dash__cartela-nota">{nota}</p>}
    </div>
  );
}

/**
 * O selo que diz por qual data o bloco conta.
 *
 * Existe por causa do alerta do handoff da 2.2 — é a única defesa contra somar
 * dois blocos que contam coisas diferentes.
 */
function Selo({ children }: { children: string }): JSX.Element {
  return <span className="etiqueta etiqueta--acao dash__selo">{children}</span>;
}

// -------------------------------------------------------------------- página

export function Dashboard(): JSX.Element {
  /*
   * `useState` com inicializador de função: `new Date()` avaliado uma vez, no
   * primeiro render. Fora dele, cada render criaria uma data nova e o período
   * "mês atual" se recalcularia sozinho à meia-noite, no meio de uma sessão.
   */
  const [periodo, setPeriodo] = useState<Periodo>(() => mesAtual(new Date()));

  /*
   * O servidor recusa `de > ate` com 400. Barrar aqui evita a viagem e, mais
   * importante, evita que a tela pisque um erro de API para o que é, na
   * verdade, um intervalo pela metade — o estado normal de quem está mexendo
   * nos dois campos.
   */
  const intervaloInvertido = periodoInvertido(periodo);

  /*
   * Campo de data VAZIO é o estado normal de quem está trocando o período: o
   * navegador limpa o valor antes de aceitar o novo.
   *
   * Sem esta guarda, `'' > '2026-08-26'` é falso — o intervalo não parece
   * invertido — e a tela dispara `?de=&ate=…`, que o servidor recusa com 400.
   * O usuário via um erro vermelho no meio de uma digitação que ainda nem
   * terminou. Foi o teste do intervalo invertido que expôs isto, contando uma
   * chamada a mais do que devia.
   */
  const incompleto = periodoIncompleto(periodo);

  const consulta = useQuery({
    /*
     * `de` e `ate` FAZEM PARTE da chave. O `staleTime` global é de 30s: com a
     * chave só em `['dashboard']`, trocar de período devolveria o cache do
     * período anterior — números velhos sob um filtro novo, que é o pior tipo
     * de erro numa tela de números.
     */
    queryKey: ['dashboard', periodo.de, periodo.ate],
    queryFn: async ({ signal }) => {
      /*
       * `URLSearchParams` em vez de concatenar à mão, e os nomes são exatamente
       * `de` e `ate`: o schema da rota é `.strict()` e recusa qualquer outro —
       * um `?inicio=` levaria 400 em vez de ser ignorado em silêncio.
       */
      const consultaUrl = new URLSearchParams({ de: periodo.de, ate: periodo.ate });
      return obter<ResumoDashboard>(`/api/dashboard?${consultaUrl.toString()}`, signal);
    },
    enabled: !intervaloInvertido && !incompleto,
  });

  /*
   * A guarda de `undefined` logo abaixo na cadeia não é defensividade vazia: é
   * o que prova ao compilador que `<Painel>` recebe dados completos. Os ramos
   * de `isPending`/`isError` já cobrem o caso na prática, mas o tipo de
   * `useQuery` não carrega essa dedução.
   */
  const dados = consulta.data;

  /*
   * "Vazio" é não ter havido NADA no período — nem emissão, nem orçamento
   * criado. Só `faturamento.quantidade === 0` não serve: um período com cinco
   * rascunhos e nenhuma emissão tem muito a dizer (é justamente o funil que
   * interessa), e uma tela vazia ali esconderia o dado.
   */
  const semMovimento =
    dados !== undefined && dados.faturamento.quantidade === 0 && dados.funil.criados === 0;

  /*
   * Sem `useMemo([])` aqui — e a razão é um defeito que o memo criava.
   *
   * Congelado na dependência vazia, "Mês atual" servia o mês do MOUNT para
   * sempre. Numa aba deixada aberta (que é como um dashboard vive), clicar em
   * "Mês atual" no dia seguinte devolvia `ate=ontem` e as emissões de hoje
   * ficavam fora do total, do RT e do ranking — sem nada na tela dizendo isso.
   * Na virada do mês era pior: agosto inteiro, com o botão destacado como se
   * fosse setembro.
   *
   * Recalcular por render NÃO move o período sozinho: `periodo` é state e só
   * muda por `setPeriodo`. O que passa a acompanhar o relógio é só o
   * destaque — que corretamente deixa de realçar "Mês atual" quando o dia vira.
   */
  const atalhos = { atual: mesAtual(new Date()), anterior: mesAnterior(new Date()) };

  return (
    <section className="dash">
      <header className="pagina__cabecalho">
        <div>
          <h1>Dashboard</h1>
          <p className="pagina__sub">
            Faturamento, funil e mix do período. Os valores são os congelados na emissão — os mesmos
            que saíram no PDF.
          </p>
        </div>
      </header>

      <div className="cartao dash__filtro">
        <div className="dash__atalhos" role="group" aria-label="Período">
          <button
            type="button"
            /* Sem `aria-pressed`, os dois botões soam idênticos num leitor de
               tela: "Mês atual, botão" / "Mês anterior, botão", sem dizer qual
               período está na tela. A cor sozinha não carrega essa informação. */
            aria-pressed={mesmoPeriodo(periodo, atalhos.atual)}
            className={
              mesmoPeriodo(periodo, atalhos.atual) ? 'botao botao--primario' : 'botao botao--neutro'
            }
            onClick={() => {
              setPeriodo(mesAtual(new Date()));
            }}
          >
            Mês atual
          </button>
          <button
            type="button"
            aria-pressed={mesmoPeriodo(periodo, atalhos.anterior)}
            className={
              mesmoPeriodo(periodo, atalhos.anterior)
                ? 'botao botao--primario'
                : 'botao botao--neutro'
            }
            onClick={() => {
              setPeriodo(mesAnterior(new Date()));
            }}
          >
            Mês anterior
          </button>
        </div>

        <div className="dash__datas">
          <label className="campo dash__campo">
            <span className="campo__rotulo">De</span>
            <input
              type="date"
              className="campo__entrada"
              value={periodo.de}
              onChange={(e) => {
                setPeriodo((p) => ({ ...p, de: e.target.value }));
              }}
            />
          </label>
          <label className="campo dash__campo">
            <span className="campo__rotulo">Até</span>
            <input
              type="date"
              className="campo__entrada"
              value={periodo.ate}
              onChange={(e) => {
                setPeriodo((p) => ({ ...p, ate: e.target.value }));
              }}
            />
          </label>
        </div>
      </div>

      {incompleto ? (
        <div className="cartao">
          <p className="aviso" role="alert">
            Escolha as duas datas do período para ver os números.
          </p>
        </div>
      ) : intervaloInvertido ? (
        <div className="cartao">
          <p className="aviso" role="alert">
            A data inicial ({periodo.de}) é depois da final ({periodo.ate}). Ajuste o intervalo para
            ver os números.
          </p>
        </div>
      ) : consulta.isPaused ? (
        /*
         * Sem rede, o `networkMode: 'online'` (padrão) PAUSA a query: ela não
         * dispara, `fetchStatus` vira 'paused' e `status` continua 'pending'.
         * Sem este ramo, `isPending` seria verdadeiro por tempo indefinido e a
         * tela ficaria girando o spinner para sempre — sem erro, sem botão,
         * sem dizer que o problema é a conexão. Não há o que repetir aqui: o
         * TanStack retoma sozinho quando a rede volta.
         */
        <div className="cartao">
          <Vazio
            titulo="Sem conexão"
            descricao="Os números aparecem assim que a conexão com o servidor voltar."
          />
        </div>
      ) : consulta.isPending ? (
        <div className="cartao">
          <Carregando rotulo="Carregando o dashboard…" />
        </div>
      ) : consulta.isError ? (
        <div className="cartao">
          <Erro
            erro={consulta.error}
            aoRepetir={() => {
              void consulta.refetch();
            }}
          />
        </div>
      ) : dados === undefined ? null : semMovimento ? (
        <div className="cartao">
          <Vazio
            titulo="Nenhum orçamento neste período"
            descricao="Não houve emissão nem orçamento criado entre as datas escolhidas. Experimente o mês anterior ou amplie o intervalo."
          />
        </div>
      ) : (
        <Painel dados={dados} />
      )}
    </section>
  );
}

/**
 * O conteúdo, quando há o que mostrar.
 *
 * Componente à parte por dois motivos. O primeiro é o tipo: recebendo `dados`
 * por prop, ele não depende de o compilador deduzir que a consulta terminou
 * bem — a garantia está na assinatura. O segundo é que a página passa a caber
 * na tela: o que sobra lá é filtro, estado e decisão, e o desenho fica aqui.
 */
function Painel({ dados }: { dados: ResumoDashboard }): JSX.Element {
  return (
    <>
      <section className="dash__bloco" aria-labelledby="dash-faturamento">
        <header className="dash__bloco-cabecalho">
          <h2 id="dash-faturamento">Faturamento no período</h2>
          <Selo>por data de emissão</Selo>
        </header>
        <div className="dash__cartelas">
          <Cartela
            rotulo="Total emitido"
            unidade="R$"
            valor={moedaBr(dados.faturamento.totalEmitido)}
            nota={`${String(dados.faturamento.quantidade)} ${
              dados.faturamento.quantidade === 1 ? 'orçamento emitido' : 'orçamentos emitidos'
            }`}
          />
          <Cartela
            rotulo="Ticket médio"
            unidade="R$"
            valor={moedaBr(dados.faturamento.ticketMedio)}
          />
          <Cartela
            rotulo="RT gerado"
            unidade="R$"
            valor={moedaBr(dados.faturamento.rtTotal)}
            nota="Comissão do representante"
          />
          <Cartela
            rotulo="Frete gerado"
            unidade="R$"
            valor={moedaBr(dados.faturamento.freteTotal)}
          />
        </div>
      </section>

      <section className="dash__bloco" aria-labelledby="dash-funil">
        <header className="dash__bloco-cabecalho">
          <h2 id="dash-funil">Funil</h2>
          <Selo>orçamentos criados no período</Selo>
        </header>
        <p className="dash__explica">
          Esta é uma <strong>coorte</strong>: dos orçamentos <em>criados</em> entre as datas,
          quantos viraram emissão. Um orçamento criado antes e emitido agora conta no faturamento
          acima, mas não aqui.
        </p>
        <div className="dash__cartelas">
          <Cartela rotulo="Criados" valor={String(dados.funil.criados)} />
          <Cartela rotulo="Emitidos" valor={String(dados.funil.emitidos)} />
          <Cartela rotulo="Ainda em rascunho" valor={String(dados.funil.rascunhos)} />
          <Cartela
            rotulo="Taxa de conversão"
            valor={percentualBr(dados.funil.taxaConversao)}
            nota="Emitidos ÷ criados"
          />
        </div>
      </section>

      <section className="dash__bloco" aria-labelledby="dash-mix">
        <header className="dash__bloco-cabecalho">
          <h2 id="dash-mix">Mix</h2>
          <Selo>por data de emissão</Selo>
        </header>
        <div className="dash__colunas">
          <div className="cartao dash__quadro" role="region" aria-labelledby="dash-mix-tabela">
            <h3 id="dash-mix-tabela">Por tabela</h3>
            {dados.mixPorTabela.length === 0 ? (
              <p className="dash__vazio-inline">Nada emitido neste período.</p>
            ) : (
              <Barras fatias={dados.mixPorTabela} mapa={ROTULO_TABELA} />
            )}
          </div>
          <div className="cartao dash__quadro" role="region" aria-labelledby="dash-mix-pagamento">
            <h3 id="dash-mix-pagamento">Por forma de pagamento</h3>
            {dados.mixPorPagamento.length === 0 ? (
              <p className="dash__vazio-inline">Nada emitido neste período.</p>
            ) : (
              <Barras fatias={dados.mixPorPagamento} mapa={ROTULO_MIX_PAGAMENTO} />
            )}
          </div>
        </div>
      </section>

      <section className="dash__bloco" aria-labelledby="dash-produtos">
        <header className="dash__bloco-cabecalho">
          <h2 id="dash-produtos">Produtos mais vendidos</h2>
          <Selo>por data de emissão</Selo>
        </header>
        <div className="cartao dash__quadro">
          {dados.rankingProdutos.length === 0 ? (
            <p className="dash__vazio-inline">Nada emitido neste período.</p>
          ) : (
            <div
              className="dash__rolagem"
              /* Rolável precisa ser focável: são até 10 produtos, o container
                 rola, e sem `tabIndex` quem navega por teclado não alcança as
                 duas últimas linhas do ranking. */
              tabIndex={0}
              role="region"
              aria-label="Ranking de produtos"
            >
              <table className="tabela tabela--cards">
                <thead>
                  <tr>
                    <th scope="col">Referência</th>
                    <th scope="col">Formato</th>
                    <th scope="col" className="num">
                      Orçamentos
                    </th>
                    <th scope="col" className="num">
                      Quantidade
                    </th>
                    <th scope="col" className="num">
                      Valor (R$)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dados.rankingProdutos.map((p) => (
                    <tr key={p.referencia}>
                      <td data-rotulo="Referência">{p.referencia}</td>
                      <td data-rotulo="Formato">{p.formato ?? '—'}</td>
                      <td className="num" data-rotulo="Orçamentos">
                        {p.vezes}
                      </td>
                      <td className="num" data-rotulo="Quantidade">
                        {quantidadeBr(p.quantidade)}
                      </td>
                      <td className="num" data-rotulo="Valor" data-destaque="sim">
                        {moedaBr(p.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/*
                O total desta tabela pode não fechar com "Total emitido" na casa
                dos centavos, e isso é esperado: o ranking soma item a item
                (`quote_item.total_item`) enquanto o card usa o total congelado
                na emissão. A divergência é de arredondamento por item — está
                documentada na Story 2.2. Dizer isso na tela evita a pergunta.
              */}
          <p className="dash__rodape-nota">
            Somado item a item — pode diferir do total emitido em centavos, por arredondamento.
          </p>
        </div>
      </section>
    </>
  );
}
